<template>
  <div class="api-test-preview-step">
    <!-- Test Connection & Preview Actions -->
    <div class="d-flex align-center justify-center mb-4" style="gap: 12px">
      <v-btn
        color="primary"
        :loading="testing"
        :disabled="!canTest || previewing"
        @click="testConnection"
      >
        <v-icon left>mdi-connection</v-icon>
        Test Connection
      </v-btn>
      <v-btn
        v-if="testResult && testResult.success"
        outlined
        color="primary"
        :loading="previewing"
        :disabled="testing"
        @click="fetchPreview"
      >
        <v-icon left>mdi-table-eye</v-icon>
        Fetch Preview
      </v-btn>
      <v-btn
        v-if="previewing"
        text
        small
        color="error"
        @click="cancelPreview"
      >
        <v-icon left small>mdi-close-circle</v-icon>
        Cancel
      </v-btn>
    </div>

    <!-- Test Result -->
    <v-alert
      v-if="testResult"
      :type="testResult.success ? 'success' : 'error'"
      outlined
      dense
      class="mb-4"
    >
      <div class="d-flex align-center justify-space-between">
        <div>
          <span class="font-weight-medium">
            {{ testResult.success ? 'Connection Successful' : 'Connection Failed' }}
          </span>
          <span v-if="testResult.errorMessage" class="ml-2 text-caption">
            — {{ testResult.errorMessage }}
          </span>
        </div>
        <div v-if="testResult.success" class="d-flex align-center" style="gap: 4px">
          <v-chip x-small class="mr-1" outlined>{{ testResult.statusCode }}</v-chip>
          <v-chip x-small class="mr-1" outlined>{{ testResult.responseTimeMs }}ms</v-chip>
          <v-chip x-small outlined>{{ formatBytes(testResult.responseSizeBytes) }}</v-chip>
        </div>
      </div>
    </v-alert>

    <!-- Skip preview hint -->
    <v-alert
      v-if="testResult && testResult.success && !previewData && !previewing"
      type="info"
      outlined
      dense
      class="mb-4"
    >
      <span class="text-caption">
        Connection verified. You can click <strong>Fetch Preview</strong> to see sample data,
        or skip it and proceed to the next step.
      </span>
    </v-alert>

    <!-- Preview cancelled / timed out -->
    <v-alert
      v-if="previewCancelled"
      type="warning"
      outlined
      dense
      class="mb-4"
    >
      <span class="text-caption">
        Preview fetch was cancelled. You can still proceed to the next step — the data will be fetched during the first import.
      </span>
    </v-alert>

    <!-- Response Data Path Refinement -->
    <v-expand-transition>
      <div v-if="testResult && testResult.success" class="mb-4">
        <v-text-field
          :value="config.responseDataPath"
          label="Response Data Path"
          placeholder="e.g. data.items, results"
          hint="Adjust if preview doesn't show expected data"
          persistent-hint
          outlined
          dense
          @input="$emit('update:responseDataPath', $event)"
        >
          <template v-slot:append>
            <v-btn icon x-small @click="fetchPreview" :loading="previewing">
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </div>
    </v-expand-transition>

    <!-- Preview Data Table -->
    <v-expand-transition>
      <div v-if="previewData && previewData.headers && previewData.headers.length">
        <div class="d-flex align-center justify-space-between mb-2">
          <span class="text-subtitle-2 font-weight-medium">
            Data Preview
          </span>
          <div class="d-flex align-center">
            <v-btn
              x-small
              text
              color="primary"
              class="mr-3"
              @click="showResponseDialog = true"
            >
              <v-icon x-small left>mdi-code-json</v-icon>
              Response
            </v-btn>
            <span class="text-caption text--secondary">
              {{ previewData.data.length }} rows shown
              <span v-if="previewData.totalRecords">
                of {{ previewData.totalRecords }} total
              </span>
            </span>
          </div>
        </div>

        <v-data-table
          :headers="previewHeaders"
          :items="previewData.data"
          dense
          disable-pagination
          hide-default-footer
          class="elevation-1 preview-table"
          :mobile-breakpoint="0"
        >
          <template v-slot:item="{ item }">
            <tr>
              <td
                v-for="header in previewData.headers"
                :key="header"
                class="text-caption text-truncate"
                style="max-width: 200px"
              >
                {{ formatCellValue(item[header]) }}
              </td>
            </tr>
          </template>
        </v-data-table>

      </div>
    </v-expand-transition>

    <!-- Response Dialog (Structure + Full Data tabs) -->
    <v-dialog v-model="showResponseDialog" max-width="900" scrollable content-class="json-dialog">
      <v-card class="json-dialog-card">
        <v-card-title class="d-flex align-center justify-space-between py-2">
          <span>Response</span>
          <div>
            <v-btn icon small class="mr-1" @click="copyCurrentTab">
              <v-icon small>{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            </v-btn>
            <v-btn icon small @click="showResponseDialog = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-tabs v-model="responseTab" dense height="36">
          <v-tab>Structure</v-tab>
          <v-tab>Full Data</v-tab>
        </v-tabs>
        <v-divider />
        <v-card-text class="pa-0">
          <v-tabs-items v-model="responseTab">
            <v-tab-item>
              <vue-json-pretty
                v-if="responseStructure"
                :data="responseStructure"
                :deep="3"
                show-length
                show-icon
                :collapsed-on-click-brackets="true"
                class="json-viewer-container"
              />
            </v-tab-item>
            <v-tab-item>
              <vue-json-pretty
                v-if="previewData && previewData.data"
                :data="previewData.data"
                :deep="2"
                show-length
                show-icon
                :collapsed-on-click-brackets="true"
                class="json-viewer-container"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Empty state -->
    <div
      v-if="!testResult && !testing"
      class="text-center py-8 text--secondary"
    >
      <v-icon size="48" color="grey lighten-1">mdi-api</v-icon>
      <div class="mt-2 text-body-2">
        Click "Test Connection" to verify your API endpoint
      </div>
    </div>
  </div>
</template>

<script>
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';

export default {
  name: 'SharedApiTestPreviewStep',
  components: { VueJsonPretty },
  props: {
    config: {
      type: Object,
      required: true,
    },
    /** Service object with testConnection(config) and fetchPreview(config, maxRows, { signal }) */
    apiService: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      testing: false,
      previewing: false,
      previewCancelled: false,
      previewAbortController: null,
      showResponseDialog: false,
      responseTab: 0,
      copied: false,
      testResult: null,
      previewData: null,
    };
  },
  computed: {
    canTest() {
      return this.config.url && this.config.url.trim().length > 0;
    },
    previewHeaders() {
      if (!this.previewData || !this.previewData.headers) return [];
      return this.previewData.headers.map(h => ({
        text: h,
        value: h,
        sortable: false,
      }));
    },
    responseStructure() {
      if (!this.previewData || !this.previewData.data || !this.previewData.data.length) return null;
      return this.extractStructure(this.pickMostPopulatedRecord(this.previewData.data));
    },
  },
  methods: {
    /** Reset local state (called by parent wizard on open/close) */
    reset() {
      this.testResult = null;
      this.previewData = null;
      this.testing = false;
      this.previewing = false;
      this.previewCancelled = false;
    },

    async testConnection() {
      this.testing = true;
      this.previewCancelled = false;
      try {
        const result = await this.apiService.testConnection(this.config);
        this.testResult = result;
        if (!result.success) return;
        this.$emit('test-success', result);
      } catch (err) {
        this.testResult = { success: false, errorMessage: err.response?.data?.message || 'Connection test failed' };
        this.$dialog.notify.error(
          err.response?.data?.message || 'Connection test failed'
        );
      } finally {
        this.testing = false;
      }
    },

    async fetchPreview() {
      this.previewing = true;
      this.previewCancelled = false;
      this.previewAbortController = new AbortController();
      try {
        const result = await this.apiService.fetchPreview(
          this.config,
          20,
          { signal: this.previewAbortController.signal },
        );
        this.previewData = result;
        this.$emit('preview-loaded', result);
      } catch (err) {
        if (err.name === 'AbortError' || err.message === 'canceled') {
          return;
        }
        this.$dialog.notify.error(
          err.response?.data?.message || 'Failed to fetch preview data'
        );
      } finally {
        this.previewing = false;
        this.previewAbortController = null;
      }
    },

    cancelPreview() {
      if (this.previewAbortController) {
        this.previewAbortController.abort();
      }
      this.previewing = false;
      this.previewCancelled = true;
    },

    formatBytes(bytes) {
      if (!bytes) return '0 B';
      const sizes = ['B', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    },

    formatCellValue(value) {
      if (value === null || value === undefined) return '—';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    },

    async copyCurrentTab() {
      const data = this.responseTab === 0
        ? this.responseStructure
        : this.previewData?.data;
      if (!data) return;
      try {
        await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      } catch (err) {
        this.$dialog.notify.error('Failed to copy to clipboard');
      }
    },

    pickMostPopulatedRecord(records) {
      if (records.length === 1) return records[0];
      let best = records[0];
      let bestScore = 0;
      for (const record of records) {
        let score = 0;
        for (const key of Object.keys(record)) {
          const val = record[key];
          if (val !== null && val !== undefined && val !== '') score++;
        }
        if (score > bestScore) {
          bestScore = score;
          best = record;
        }
      }
      return best;
    },

    extractStructure(obj) {
      if (obj === null || obj === undefined) return '(null)';
      if (Array.isArray(obj)) {
        return obj.length ? [this.extractStructure(obj[0])] : ['(empty)'];
      }
      if (typeof obj === 'object') {
        const structure = {};
        for (const key of Object.keys(obj)) {
          const val = obj[key];
          if (val === null || val === undefined) structure[key] = '(null)';
          else if (Array.isArray(val)) structure[key] = this.extractStructure(val);
          else if (typeof val === 'object') structure[key] = this.extractStructure(val);
          else structure[key] = this.detectType(val);
        }
        return structure;
      }
      return this.detectType(obj);
    },

    detectType(val) {
      if (typeof val === 'boolean') return '(boolean)';
      if (typeof val === 'number') {
        return Number.isInteger(val) ? '(integer)' : '(decimal)';
      }
      if (typeof val !== 'string') return `(${typeof val})`;

      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(val)) return '(datetime)';
      if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(val)) return '(date)';
      if (/^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}$/.test(val) && !isNaN(Date.parse(val))) return '(date)';
      if (/^(true|false|yes|no)$/i.test(val)) return '(boolean)';
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val)) return '(uuid)';
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return '(email)';
      if (/^-?\d+(\.\d+)?$/.test(val)) return '(numeric string)';

      return '(text)';
    },
  },
};
</script>

<style scoped>
.preview-table {
  max-height: 400px;
  overflow-y: auto;
}

.json-dialog-card {
  min-width: 500px;
  min-height: 400px;
}

.json-viewer-container {
  padding: 16px;
  font-size: 13px;
  max-height: 60vh;
  overflow-y: auto;
}
</style>

<!-- Unscoped: v-dialog content-class renders outside this component's DOM -->
<style>
.json-dialog {
  min-width: 400px;
}
</style>
